import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const CookieConsent = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('joinzy-cookie-consent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setIsVisible(true), 1500);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    localStorage.setItem('joinzy-cookie-consent', JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    };
    localStorage.setItem('joinzy-cookie-consent', JSON.stringify(onlyNecessary));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('joinzy-cookie-consent', JSON.stringify(preferences));
    setIsVisible(false);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[90] p-4 md:p-6"
        data-testid="cookie-consent"
      >
        <div className="max-w-4xl mx-auto">
          {!showSettings ? (
            // Main Cookie Banner
            <div className="bg-[#0B0E14]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#008080]/20 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-[#00E5FF]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-montserrat font-bold text-white text-lg mb-2">
                    {t('cookies.title')}
                  </h3>
                  <p className="text-white/60 text-sm mb-4">
                    {t('cookies.description')}
                  </p>
                  
                  <div className="flex flex-wrap gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleAcceptAll}
                      className="px-6 py-2.5 bg-[#008080] hover:bg-[#009090] rounded-full text-white font-semibold text-sm transition-colors"
                      data-testid="cookie-accept-all"
                    >
                      {t('cookies.acceptAll')}
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleRejectAll}
                      className="px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white font-semibold text-sm transition-colors"
                      data-testid="cookie-reject-all"
                    >
                      {t('cookies.rejectAll')}
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowSettings(true)}
                      className="px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white font-semibold text-sm transition-colors flex items-center gap-2"
                      data-testid="cookie-settings"
                    >
                      <Settings className="w-4 h-4" />
                      {t('cookies.customize')}
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Cookie Settings Panel
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-[#0B0E14]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-montserrat font-bold text-white text-lg">
                  {t('cookies.settingsTitle')}
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-white/70" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <CookieOption
                  title={t('cookies.necessary')}
                  description={t('cookies.necessaryDesc')}
                  checked={preferences.necessary}
                  disabled={true}
                />

                {/* Analytics Cookies */}
                <CookieOption
                  title={t('cookies.analytics')}
                  description={t('cookies.analyticsDesc')}
                  checked={preferences.analytics}
                  onChange={(checked) => setPreferences({ ...preferences, analytics: checked })}
                />

                {/* Marketing Cookies */}
                <CookieOption
                  title={t('cookies.marketing')}
                  description={t('cookies.marketingDesc')}
                  checked={preferences.marketing}
                  onChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
                />

                {/* Personalization Cookies */}
                <CookieOption
                  title={t('cookies.personalization')}
                  description={t('cookies.personalizationDesc')}
                  checked={preferences.personalization}
                  onChange={(checked) => setPreferences({ ...preferences, personalization: checked })}
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSavePreferences}
                  className="px-6 py-2.5 bg-[#008080] hover:bg-[#009090] rounded-full text-white font-semibold text-sm transition-colors flex items-center gap-2"
                  data-testid="cookie-save-preferences"
                >
                  <Check className="w-4 h-4" />
                  {t('cookies.savePreferences')}
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white font-semibold text-sm transition-colors"
                >
                  {t('cookies.acceptAll')}
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Cookie Option Toggle Component
const CookieOption = ({ title, description, checked, onChange, disabled = false }) => (
  <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
    <div className="flex-1">
      <h4 className="font-semibold text-white text-sm mb-1">{title}</h4>
      <p className="text-white/50 text-xs">{description}</p>
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange && onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className={`w-11 h-6 rounded-full peer transition-colors ${
        disabled 
          ? 'bg-[#008080]/50 cursor-not-allowed' 
          : checked 
            ? 'bg-[#008080]' 
            : 'bg-white/20'
      } peer-focus:outline-none peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all`}>
      </div>
    </label>
  </div>
);
